import { Button } from "@ui/components/button";
import { CreditCard, Check, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAiReportPurchaseStatus, formatAiReportPrice, usePurchaseFlow, getAiReportPrice } from "../lib/api";
import Link from "next/link";
import { useAnalytics } from "@analytics";
import { AI_REPORT_EVENTS } from "@repo/analytics";
import type { AIReportPurchaseInitiatedData } from "@repo/analytics";

interface PurchaseButtonProps {
	reportPublicToken: string;
	className?: string;
	size?: "sm" | "md" | "lg" | "icon";
	variant?: "primary" | "secondary" | "outline" | "ghost" | "link" | "light" | "error";
	disabled?: boolean;
	style?: React.CSSProperties;
	/**
	 * If true, button will directly invoke Stripe checkout.
	 * If false (default), button will link to the unlock page.
	 */
	directCheckout?: boolean;
	reportId?: string;
	domain?: string;
	consistencyScore?: number;
	reportStatus?: string;
}

export function PurchaseButton({ 
	reportPublicToken, 
	className, 
	size = "md",
	variant = "primary",
	disabled = false,
	style,
	directCheckout = false,
	reportId,
	domain,
	consistencyScore,
	reportStatus
}: PurchaseButtonProps) {
	const t = useTranslations();
	const { trackEvent } = useAnalytics();
	const { data: purchaseStatus } = useAiReportPurchaseStatus(reportPublicToken);
	const formattedPrice = formatAiReportPrice();
	const { handlePurchase, isLoading } = usePurchaseFlow(reportPublicToken);

	const handlePurchaseWithTracking = async () => {
		// Track purchase initiation
		if (reportId && domain) {
			const price = getAiReportPrice();
			trackEvent(AI_REPORT_EVENTS.REPORT_PURCHASE_INITIATED, {
				reportId,
				domain,
				consistencyScore: consistencyScore || 0,
				price: price.amount,
				currency: price.currency,
			} as AIReportPurchaseInitiatedData);
		}
		
		await handlePurchase();
	};

	// Don't show button if already purchased
	if (purchaseStatus?.isPurchased) {
		return (
			<Button variant="outline" size={size} className={className} disabled style={style}>
				<Check className="mr-2 h-4 w-4 text-green-600" />
				{t("marketing.aiCreditReport.purchase.alreadyPurchased")}
			</Button>
		);
	}

	// Disable button if report is not complete
	const isReportIncomplete = reportStatus && reportStatus !== "complete";
	const isButtonDisabled = disabled || isReportIncomplete;

	// If directCheckout is true, invoke Stripe directly
	if (directCheckout) {
		return (
			<Button 
				variant={variant} 
				size={size} 
				className={className}
				disabled={isButtonDisabled || isLoading}
				onClick={handlePurchaseWithTracking}
				style={style}
				title={isReportIncomplete ? "Report must be complete before purchase" : undefined}
			>
				{isLoading ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						{t("marketing.aiCreditReport.purchase.processing")}
					</>
				) : (
					<>
						<CreditCard className="mr-2 h-4 w-4" />
						{t("marketing.aiCreditReport.purchase.unlockReport")} - {formattedPrice}
					</>
				)}
			</Button>
		);
	}

	// Otherwise, link to the unlock page
	return (
		<Button 
			variant={variant} 
			size={size} 
			className={className}
			disabled={!!isButtonDisabled}
			style={style}
			title={isReportIncomplete ? "Report must be complete before purchase" : undefined}
			asChild={!isButtonDisabled}
		>
			{!isButtonDisabled ? (
				<Link href={`/reports/${reportPublicToken}/unlock`}>
					<CreditCard className="mr-2 h-4 w-4" />
					{t("marketing.aiCreditReport.purchase.unlockReport")}
				</Link>
			) : (
				<>
					<CreditCard className="mr-2 h-4 w-4" />
					{t("marketing.aiCreditReport.purchase.unlockReport")}
				</>
			)}
		</Button>
	);
}
